import React, { useEffect, useState } from 'react';
import {
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import adminBehaviorService, { BehaviorRange, SlowOperations } from '../../../services/api/adminBehaviorService';
import { formatMs, formatNumber, formatPercent } from './format';

interface Props {
  range: BehaviorRange;
}

/**
 * 生成ジョブの待ち時間分布（job テーブル由来）と、3 秒超の API 呼び出しの分布。
 */
const SlowOperationsTab: React.FC<Props> = ({ range }) => {
  const [data, setData] = useState<SlowOperations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminBehaviorService
      .getSlowOperations(range)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError('処理時間の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <TaskySpinner />
      </div>
    );
  }
  if (error) return <TaskyErrorState title={error} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-sm font-semibold text-dark">生成ジョブの待ち時間（開始 → 完了）</h3>
        {data.generations.length === 0 ? (
          <TaskyEmptyState title="この期間の生成ジョブはありません" />
        ) : (
          <div className="overflow-x-auto">
            <TaskyTable className="min-w-[640px]">
              <TaskyTableHead>
                <TaskyTableRow>
                  <TaskyTableHeaderCell>種別</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>件数</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>失敗</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>失敗率</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>p50</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>p90</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>最大</TaskyTableHeaderCell>
                </TaskyTableRow>
              </TaskyTableHead>
              <TaskyTableBody>
                {data.generations.map((row) => (
                  <TaskyTableRow key={row.type}>
                    <TaskyTableCell className="font-mono text-xs">{row.type}</TaskyTableCell>
                    <TaskyTableCell>{formatNumber(row.count)}</TaskyTableCell>
                    <TaskyTableCell>{formatNumber(row.failed)}</TaskyTableCell>
                    <TaskyTableCell className={row.failed > 0 ? 'text-destructive' : ''}>
                      {formatPercent(row.count > 0 ? (row.failed / row.count) * 100 : 0)}
                    </TaskyTableCell>
                    <TaskyTableCell>{formatMs(row.p50_ms)}</TaskyTableCell>
                    <TaskyTableCell>{formatMs(row.p90_ms)}</TaskyTableCell>
                    <TaskyTableCell>{formatMs(row.max_ms)}</TaskyTableCell>
                  </TaskyTableRow>
                ))}
              </TaskyTableBody>
            </TaskyTable>
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-dark">遅い API 呼び出し（3 秒超）</h3>
        {data.api.length === 0 ? (
          <TaskyEmptyState title="この期間に 3 秒を超えた API 呼び出しはありません" />
        ) : (
          <div className="overflow-x-auto">
            <TaskyTable className="min-w-[560px]">
              <TaskyTableHead>
                <TaskyTableRow>
                  <TaskyTableHeaderCell>API</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>件数</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>p50</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>p90</TaskyTableHeaderCell>
                </TaskyTableRow>
              </TaskyTableHead>
              <TaskyTableBody>
                {data.api.map((row) => (
                  <TaskyTableRow key={row.event_name}>
                    <TaskyTableCell className="font-mono text-xs">{row.event_name}</TaskyTableCell>
                    <TaskyTableCell>{formatNumber(row.count)}</TaskyTableCell>
                    <TaskyTableCell>{formatMs(row.p50_ms)}</TaskyTableCell>
                    <TaskyTableCell>{formatMs(row.p90_ms)}</TaskyTableCell>
                  </TaskyTableRow>
                ))}
              </TaskyTableBody>
            </TaskyTable>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlowOperationsTab;
